import { createContext, useContext, useState, useEffect } from 'react';
import { useAccount, useSignMessage, useDisconnect } from 'wagmi';
import { toast } from '../components/Toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { address, isConnected, chainId } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();

  const API_BASE = 'https://api.zpaynow.com';

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('zeropay_token');
    const userData = localStorage.getItem('zeropay_user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Auto-disconnect if wallet disconnects but user is logged in
  useEffect(() => {
    if (!isConnected && user) {
      // Wallet was disconnected, but we still have a session
      // Optionally logout or just keep the session
    }
  }, [isConnected, user]);

  const getNonce = async () => {
    const response = await fetch(`${API_BASE}/nonce`);
    if (!response.ok) {
      toast.error('Failed to connect to ZeroPay service. Please try again.');
      throw new Error('Failed to get nonce');
    }
    const data = await response.json();
    return data.nonce;
  };

  const login = async () => {
    if (!isConnected || !address) {
      toast.error('Please connect your wallet first.');
      throw new Error('Wallet not connected');
    }

    setIsLoading(true);

    try {
      // Get nonce from server
      const nonce = await getNonce();

      // Create SIWE message using the exact format from client.rs
      // Use the current chain ID from user's wallet (no need to switch networks)
      const messageString = `${window.location.host} wants you to sign in with your Ethereum account:
${address}

Sign in to ZPayNow service

URI: https://zpaynow.com
Version: 1
Chain ID: ${chainId || 1}
Nonce: ${nonce}
Issued At: ${new Date().toISOString()}`;

      // Sign message using wagmi
      const signature = await signMessageAsync({ message: messageString });

      // Send to server for verification
      const response = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: messageString,
          signature,
        }),
      });

      if (!response.ok) {
        toast.error('Authentication failed. Please try again.');
        throw new Error('Login failed');
      }

      const data = await response.json();

      if (data.token) {
        const userData = {
          address: data.account,
          name: data.name,
          apikey: data.apikey,
          webhook: data.webhook,
          eth: data.eth,
          chains: data.chains || [],
          token: data.token,
        };

        localStorage.setItem('zeropay_token', data.token);
        localStorage.setItem('zeropay_user', JSON.stringify(userData));
        setUser(userData);
        toast.success('Successfully signed in with Ethereum!');
      } else {
        toast.error(data.error || 'Authentication failed. Please try again.');
        throw new Error(data.error || 'Login failed');
      }
    } catch (err) {
      if (err.message.includes('User rejected') || err.message.includes('User denied')) {
        toast.error('Wallet connection was cancelled.');
      } else if (!err.message.includes('Wallet not connected') && !err.message.includes('Failed to connect')) {
        toast.error('Authentication failed. Please check your wallet and try again.');
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('zeropay_token');
    localStorage.removeItem('zeropay_user');
    setUser(null);
    disconnect(); // Disconnect wallet
    toast.success('Successfully signed out.');

    // Redirect to platform page
    setTimeout(() => {
      window.location.href = '/platform';
    }, 500); // Small delay to show the success message
  };

  const updateProfile = async (updates) => {
    const token = localStorage.getItem('zeropay_token');
    if (!token) {
      toast.error('Please sign in to update your profile.');
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/merchants/info`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      toast.error('Failed to update profile. Please try again.');
      throw new Error('Failed to update profile');
    }

    const data = await response.json();

    // Update local user data, ensuring chains field is preserved/updated
    const updatedUser = {
      ...user,
      ...data,
      chains: data.chains || user.chains || []
    };
    localStorage.setItem('zeropay_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    toast.success('Profile updated successfully!');

    return data;
  };

  const regenerateApiKey = async () => {
    const token = localStorage.getItem('zeropay_token');
    if (!token) {
      toast.error('Please sign in to regenerate your API key.');
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE}/merchants/regenerate-apikey`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      toast.error('Failed to regenerate API key. Please try again.');
      throw new Error('Failed to regenerate API key');
    }

    const data = await response.json();

    // Update local user data with new API key
    const updatedUser = { ...user, apikey: data.apikey };
    localStorage.setItem('zeropay_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    toast.success('API key regenerated successfully!');

    return data.apikey;
  };

  const value = {
    user,
    isLoading,
    login,
    logout,
    updateProfile,
    regenerateApiKey,
    isAuthenticated: !!user,
    walletAddress: address,
    walletConnected: isConnected,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};